/** Traduzione di un drill-down statistico in un URL di lista.
 *
 * Il backend garantisce che i `params` siano query param che quella lista legge
 * davvero dall'URL, ma **non** sa dove vivono le liste: le rotte sono una
 * conoscenza del frontend. In particolare i segmenti del flusso RDI *sono* rotte
 * distinte (`/`, `/allarmi`, `/hyper`) e il sotto-tipo arriva al componente come
 * prop, non dalla query string — quindi un `sub_type=allarme` nell'URL sarebbe
 * silenziosamente ignorato. Su DDT invece il segmento è un vero filtro
 * (`document_type`), quindi viaggia nei parametri.
 *
 * Questa asimmetria è il motivo per cui la mappa vive qui e non nel backend.
 */

import type { StatsDrill, StatsFlow } from "../api/types";

/** Rotta della lista per un flusso e un segmento. */
export function segmentRoute(flow: string, segment?: string | null): string {
  if (flow === "rdi") {
    // I segmenti RDI sono rotte: la lista riceve il sotto-tipo come prop.
    if (segment === "allarmi") return "/allarmi";
    if (segment === "hyper") return "/hyper";
    // `classiche` e "tutti insieme" cadono entrambi sull'indice, che rende la
    // lista con excludeSubType="allarme,hyperfabbricati" (App.tsx:89).
    return "/";
  }
  if (flow === "ddt") return "/ddt";
  if (flow === "bsc") return "/bsc";
  if (flow === "rinascente") return "/rinascente";
  if (flow === "deleghe") return "/deleghe";
  return "/";
}

/** Segmenti che si esprimono come query param invece che come rotta. */
const SEGMENT_AS_PARAM: Record<string, string> = {
  // Il tipo documento è un filtro reale della lista DDT (ADR 0005).
  ddt: "document_type",
};

/** URL completo che riproduce il numero a cui il drill è agganciato. */
export function drillHref(drill: StatsDrill): string {
  const path = segmentRoute(drill.flow, drill.segment);
  const params = new URLSearchParams(drill.params);
  const paramName = SEGMENT_AS_PARAM[drill.flow];
  if (paramName && drill.segment) params.set(paramName, drill.segment);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Rotta della pagina statistiche di un flusso. */
export function statsRoute(flow: StatsFlow): string {
  return flow === "rdi" ? "/stats" : `/${flow}/stats`;
}
